/**
 * downloadLocation.js — File System Access API wrapper for persisting
 * user-chosen download directories across sessions.
 *
 * Handles are stored in IndexedDB (not localStorage — FileSystemDirectoryHandle
 * is not JSON-serializable).
 *
 * Keys:
 *   'app-default'     — application-wide fallback
 *   'local-reports'   — override for reports generated on this machine
 *   'remote-reports'  — override for re-downloads from other machines
 *
 * Resolution order at save time:
 *   1. type-specific key ('local-reports' or 'remote-reports')
 *   2. 'app-default'
 *   3. showSaveFilePicker / blob-URL fallback (existing behavior)
 *
 * Only works in Chromium (Chrome/Edge). Firefox/Safari fall through silently.
 */

const DB_NAME   = 'dawn-download-locations';
const DB_VER    = 1;
const STORE     = 'handles';

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });

const dbGet = async (key) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror   = () => reject(req.error);
  });
};

const dbPut = async (key, value) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
};

const dbDelete = async (key) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(key);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
};

/**
 * Prompt user to pick a directory, store the handle.
 * Must be called from a user gesture (button click).
 * @param {'app-default'|'local-reports'|'remote-reports'} key
 * @returns {string|null} The folder name, or null if cancelled/unsupported
 */
export const setDownloadLocation = async (key) => {
  if (!window.showDirectoryPicker) return null;
  try {
    const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
    await dbPut(key, handle);
    return handle.name;
  } catch (err) {
    if (err.name === 'AbortError') return null;
    return null;
  }
};

/**
 * Retrieve stored handle for the key, verifying write permission.
 * Falls back to 'app-default' if the specific key has no handle.
 * Returns null if no handle available or permission denied.
 * @param {'local-reports'|'remote-reports'} key
 * @returns {FileSystemDirectoryHandle|null}
 */
export const getDownloadHandle = async (key) => {
  if (!window.showDirectoryPicker) return null;
  try {
    let handle = await dbGet(key);
    if (!handle && key !== 'app-default') {
      handle = await dbGet('app-default');
    }
    if (!handle) return null;

    const perm = await handle.requestPermission({ mode: 'readwrite' });
    if (perm !== 'granted') return null;
    return handle;
  } catch {
    return null;
  }
};

/**
 * Write a Blob to a directory handle without showing a save dialog.
 * @param {FileSystemDirectoryHandle} dirHandle
 * @param {string} filename
 * @param {Blob} blob
 */
export const writeToDirectory = async (dirHandle, filename, blob) => {
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
  const writable   = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
};

/**
 * Get the display label (folder name) for a stored handle, or null.
 * @param {'app-default'|'local-reports'|'remote-reports'} key
 * @returns {string|null}
 */
export const getLocationLabel = async (key) => {
  try {
    const handle = await dbGet(key);
    return handle?.name ?? null;
  } catch {
    return null;
  }
};

/**
 * Remove a stored handle.
 * @param {'app-default'|'local-reports'|'remote-reports'} key
 */
export const clearDownloadLocation = async (key) => {
  await dbDelete(key);
};

/** True if the File System Access API is available (Chrome/Edge). */
export const isFileSystemAccessSupported = () => !!window.showDirectoryPicker;
